import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import { ErrorDeApi, peticion } from '../api/cliente.js';
import type { Cita, RespuestaDeCitas } from '../api/tipos.js';
import { separarCitas } from '../citas.js';
import { Aviso, BotonAtras, Cargando, EstadoVacio } from '../componentes/comunes.js';
import { instanteLargo, nombreDeZona } from '../fechas.js';
import { textos } from '../textos.js';

/**
 * «Mis citas» (§9).
 *
 * Quien entra con el acceso de la empresa ve **todas las citas de esa empresa en
 * esa entidad** (§3). Es el precio del acceso compartido y está asumido.
 *
 * Los plazos no se recalculan aquí: la API ya dice en `sePuedeCambiar` si esa
 * cita admite cambios, para que el frontal no reimplemente la regla y las dos
 * partes no puedan discrepar.
 */
export function PantallaMisCitas({
  zona,
  aviso,
  alDescartarAviso,
  alReprogramar,
  alVolver,
}: {
  zona: string;
  aviso?: string | null;
  alDescartarAviso?: () => void;
  alReprogramar: (cita: Cita) => void;
  alVolver: () => void;
}) {
  const clienteDeConsultas = useQueryClient();
  const [error, setError] = useState<string | null>(null);
  const [avisoLocal, setAvisoLocal] = useState<string | null>(null);
  const [seccion, setSeccion] = useState<'proximas' | 'pasadas'>('proximas');
  const [citaACancelar, setCitaACancelar] = useState<Cita | null>(null);
  const pestanaProximas = useRef<HTMLButtonElement>(null);
  const pestanaPasadas = useRef<HTMLButtonElement>(null);

  const consulta = useQuery({
    queryKey: ['citas'],
    queryFn: () => peticion<RespuestaDeCitas>('/citas'),
  });

  const cancelar = useMutation({
    mutationFn: (citaId: string) => peticion(`/citas/${citaId}`, { metodo: 'DELETE' }),
    onSuccess: async () => {
      setError(null);
      setCitaACancelar(null);
      setAvisoLocal(textos.misCitas.canceladaCorrectamente);
      await clienteDeConsultas.invalidateQueries({ queryKey: ['citas'] });
      // La agenda ha cambiado: el hueco liberado tiene que volver a ofrecerse.
      await clienteDeConsultas.invalidateQueries({ queryKey: ['huecos'] });
    },
    onError: (fallo: unknown) => {
      setError(fallo instanceof ErrorDeApi ? fallo.message : textos.errores.generico);
      setCitaACancelar(null);
    },
  });

  const citas = consulta.data?.citas ?? [];
  const { proximas, pasadas } = separarCitas(citas);
  const avisoVisible = aviso ?? avisoLocal;
  const cupo = consulta.data?.cupoMensual ?? null;
  const porcentajeDisponible =
    cupo?.limite !== null && cupo?.limite !== undefined && cupo.limite > 0
      ? Math.max(0, Math.min(100, ((cupo.restantes ?? 0) / cupo.limite) * 100))
      : 0;

  useEffect(() => {
    if (consulta.data !== undefined && proximas.length === 0 && pasadas.length > 0) {
      setSeccion('pasadas');
    }
  }, [consulta.data, pasadas.length, proximas.length]);

  function descartarAviso() {
    setAvisoLocal(null);
    alDescartarAviso?.();
  }

  function moverPestana(destino: 'proximas' | 'pasadas') {
    setSeccion(destino);
    (destino === 'proximas' ? pestanaProximas : pestanaPasadas).current?.focus();
  }

  return (
    <section className="pantalla-mis-citas" aria-labelledby="titulo-mis-citas">
      <BotonAtras alClick={alVolver} />
      <header className="cabecera-mis-citas">
        <div>
          <p className="sobretitulo">{textos.aplicacion.areaCliente}</p>
          <h2 id="titulo-mis-citas">{textos.misCitas.titulo}</h2>
          <p className="ayuda">
            {textos.misCitas.ayuda} {textos.hora.zonaHoraria} {nombreDeZona(zona)}.
          </p>
        </div>
        <button type="button" className="principal nueva-cita" onClick={alVolver}>
          <span aria-hidden="true">+</span>
          {textos.misCitas.nuevaCita}
        </button>
      </header>

      {avisoVisible !== null && (
        <div className="notificacion-exito" role="status">
          <span className="notificacion-exito-icono" aria-hidden="true">
            <svg viewBox="0 0 24 24">
              <path d="m7 12 3.2 3.2L17.5 8" />
            </svg>
          </span>
          <strong>{avisoVisible}</strong>
          <button type="button" onClick={descartarAviso} aria-label={textos.misCitas.cerrarAviso}>
            <span aria-hidden="true">×</span>
          </button>
        </div>
      )}

      {cupo !== null && cupo.limite !== null && (
        <aside className={`indicador-cupo${cupo.restantes === 0 ? ' agotado' : ''}`}>
          <div className="indicador-cupo-cabecera">
            <div className="indicador-cupo-titulo">
              <span className="indicador-cupo-icono" aria-hidden="true">
                <svg viewBox="0 0 24 24">
                  <path d="M7 3v3m10-3v3M4.5 9.5h15M6 5h12a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2Z" />
                </svg>
              </span>
              <strong>{textos.misCitas.cupoTitulo}</strong>
            </div>
            <strong
              className="indicador-cupo-cantidad"
              aria-label={textos.misCitas.cupoCantidad(cupo.restantes ?? 0, cupo.limite)}
            >
              <span aria-hidden="true">{cupo.restantes ?? 0}</span>
              <small aria-hidden="true">/</small>
              <span aria-hidden="true">{cupo.limite}</span>
            </strong>
          </div>
          <div
            className="indicador-cupo-barra"
            role="progressbar"
            aria-label={textos.misCitas.cupoTitulo}
            aria-valuemin={0}
            aria-valuemax={cupo.limite}
            aria-valuenow={cupo.restantes ?? 0}
          >
            <span style={{ width: `${porcentajeDisponible}%` }} />
          </div>
        </aside>
      )}

      {error !== null && <Aviso tipo="error">{error}</Aviso>}
      {consulta.isPending && <Cargando />}
      {consulta.isError && (
        <Aviso tipo="error">
          {textos.errores.generico}{' '}
          <button type="button" className="enlace" onClick={() => void consulta.refetch()}>
            {textos.aplicacion.reintentar}
          </button>
        </Aviso>
      )}

      {consulta.data !== undefined && citas.length === 0 && (
        <EstadoVacio titulo={textos.misCitas.ninguna} detalle={textos.misCitas.ningunaAyuda} />
      )}

      {consulta.data !== undefined && citas.length > 0 && (
        <div className="pestanas-citas" role="tablist" aria-label={textos.misCitas.titulo}>
          <button
            ref={pestanaProximas}
            type="button"
            role="tab"
            id="pestana-proximas"
            aria-selected={seccion === 'proximas'}
            aria-controls="panel-proximas"
            aria-label={`${textos.misCitas.verProximas}: ${proximas.length}`}
            tabIndex={seccion === 'proximas' ? 0 : -1}
            onClick={() => setSeccion('proximas')}
            onKeyDown={(evento) => {
              if (
                evento.key === 'ArrowRight' ||
                evento.key === 'ArrowLeft' ||
                evento.key === 'End'
              ) {
                evento.preventDefault();
                moverPestana('pasadas');
              }
            }}
          >
            {textos.misCitas.proximas}
            <span>{proximas.length}</span>
          </button>
          <button
            ref={pestanaPasadas}
            type="button"
            role="tab"
            id="pestana-anteriores"
            aria-selected={seccion === 'pasadas'}
            aria-controls="panel-anteriores"
            aria-label={`${textos.misCitas.verAnteriores}: ${pasadas.length}`}
            tabIndex={seccion === 'pasadas' ? 0 : -1}
            onClick={() => setSeccion('pasadas')}
            onKeyDown={(evento) => {
              if (
                evento.key === 'ArrowLeft' ||
                evento.key === 'ArrowRight' ||
                evento.key === 'Home'
              ) {
                evento.preventDefault();
                moverPestana('proximas');
              }
            }}
          >
            {textos.misCitas.pasadas}
            <span>{pasadas.length}</span>
          </button>
        </div>
      )}

      {consulta.data !== undefined && citas.length > 0 && (
        <section
          className="grupo-citas"
          aria-labelledby="titulo-proximas"
          hidden={seccion !== 'proximas'}
        >
          <div className="cabecera-grupo-citas">
            <h3 id="titulo-proximas">{textos.misCitas.proximas}</h3>
            <span>{proximas.length}</span>
          </div>
          <div role="tabpanel" id="panel-proximas" aria-labelledby="pestana-proximas" tabIndex={0}>
            {proximas.length === 0 ? (
              <EstadoVacio
                titulo={textos.misCitas.sinProximas}
                detalle={textos.misCitas.sinProximasAyuda}
                compacto
              />
            ) : (
              <ul className="citas">
                {proximas.map((cita) => (
                  <TarjetaDeCita
                    key={cita.id}
                    cita={cita}
                    zona={zona}
                    alCancelar={() => setCitaACancelar(cita)}
                    alReprogramar={() => alReprogramar(cita)}
                  />
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      {consulta.data !== undefined && citas.length > 0 && (
        <section
          className="grupo-citas grupo-citas-anteriores"
          aria-labelledby="titulo-anteriores"
          hidden={seccion !== 'pasadas'}
        >
          <div className="cabecera-grupo-citas">
            <h3 id="titulo-anteriores">{textos.misCitas.pasadas}</h3>
            <span>{pasadas.length}</span>
          </div>
          <div
            role="tabpanel"
            id="panel-anteriores"
            aria-labelledby="pestana-anteriores"
            tabIndex={0}
          >
            {pasadas.length === 0 ? (
              <EstadoVacio
                titulo={textos.misCitas.sinAnteriores}
                detalle={textos.misCitas.sinAnterioresAyuda}
                compacto
              />
            ) : (
              <ul className="citas">
                {pasadas.map((cita) => (
                  <TarjetaDeCita key={cita.id} cita={cita} zona={zona} pasada />
                ))}
              </ul>
            )}
          </div>
        </section>
      )}

      <div className="botonera">
        <button type="button" className="enlace" onClick={alVolver}>
          {textos.aplicacion.volver}
        </button>
      </div>

      {citaACancelar !== null && (
        <DialogoCancelacion
          cita={citaACancelar}
          zona={zona}
          cancelando={cancelar.isPending}
          alCerrar={() => setCitaACancelar(null)}
          alConfirmar={() => cancelar.mutate(citaACancelar.id)}
        />
      )}
    </section>
  );
}

function TarjetaDeCita({
  cita,
  zona,
  pasada = false,
  alCancelar,
  alReprogramar,
}: {
  cita: Cita;
  zona: string;
  pasada?: boolean;
  alCancelar?: () => void;
  alReprogramar?: () => void;
}) {
  const fecha = partesDeFecha(cita.inicio, zona);

  return (
    <li className={`cita${pasada ? ' pasada' : ''}`}>
      <div className="cita-fecha" aria-label={instanteLargo(cita.inicio, zona)}>
        <span>{fecha.semana}</span>
        <strong>{fecha.dia}</strong>
        <small>{fecha.mes}</small>
        <b>{fecha.hora}</b>
      </div>

      <div className="cita-contenido">
        <div className="cita-cabecera">
          <div>
            <span className="cita-marca">{textos.aplicacion.portal}</span>
            <h3>{cita.servicio.nombre}</h3>
          </div>
          <span className={`etiqueta ${cita.activa ? 'activa' : 'cancelada'}`}>
            <span aria-hidden="true" />
            {textos.misCitas.estados[cita.estado] ?? cita.estado}
          </span>
        </div>

        <div className="cita-datos">
          <span>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <circle cx="12" cy="12" r="9" />
              <path d="M12 7v5l3 2" />
            </svg>
            {cita.duracionMin} minutos
          </span>
          <span>
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M8.5 4.5 6 7c0 5.5 5.5 11 11 11l2.5-2.5-4-3-2 2c-2-.9-3.1-2-4-4l2-2-3-4Z" />
            </svg>
            {cita.contactoNombre} · {cita.contactoTelefono}
          </span>
        </div>

        <div className="cita-motivo">
          <span>{textos.misCitas.motivoEtiqueta}</span>
          <p>{cita.motivo}</p>
        </div>

        {!pasada && cita.activa && (
          <div className="botonera cita-acciones">
            {cita.sePuedeCambiar ? (
              <>
                <button type="button" onClick={alReprogramar}>
                  {textos.misCitas.reprogramar}
                </button>
                <button type="button" onClick={alCancelar}>
                  {textos.misCitas.cancelar}
                </button>
              </>
            ) : (
              <p className="sin-huecos">{textos.misCitas.fueraDePlazo(cita.minutosParaCambiar)}</p>
            )}
            <a className="enlace" href={`/api/v1/citas/${cita.id}/calendario.ics`} download>
              {textos.reserva.descargarIcs}
            </a>
          </div>
        )}
      </div>
    </li>
  );
}

function partesDeFecha(instante: string, zona: string) {
  const fecha = new Date(instante);
  return {
    semana: new Intl.DateTimeFormat('es-ES', { weekday: 'short', timeZone: zona })
      .format(fecha)
      .replace('.', ''),
    dia: new Intl.DateTimeFormat('es-ES', { day: '2-digit', timeZone: zona }).format(fecha),
    mes: new Intl.DateTimeFormat('es-ES', { month: 'short', timeZone: zona })
      .format(fecha)
      .replace('.', ''),
    hora: new Intl.DateTimeFormat('es-ES', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
      timeZone: zona,
    }).format(fecha),
  };
}

function DialogoCancelacion({
  cita,
  zona,
  cancelando,
  alCerrar,
  alConfirmar,
}: {
  cita: Cita;
  zona: string;
  cancelando: boolean;
  alCerrar: () => void;
  alConfirmar: () => void;
}) {
  const botonPrincipal = useRef<HTMLButtonElement>(null);
  const contenedor = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const focoAnterior =
      document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const desbordamientoAnterior = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    botonPrincipal.current?.focus();
    return () => {
      document.body.style.overflow = desbordamientoAnterior;
      focoAnterior?.focus();
    };
  }, []);

  return (
    <div
      className="fondo-dialogo"
      role="presentation"
      onMouseDown={(evento) => {
        if (evento.target === evento.currentTarget && !cancelando) alCerrar();
      }}
      onKeyDown={(evento) => {
        if (evento.key === 'Escape' && !cancelando) alCerrar();
        if (evento.key !== 'Tab') return;
        const enfocables = contenedor.current?.querySelectorAll<HTMLElement>(
          'button:not([disabled]), a[href]',
        );
        if (enfocables === undefined || enfocables.length === 0) return;
        const primero = enfocables[0];
        const ultimo = enfocables[enfocables.length - 1];
        if (evento.shiftKey && document.activeElement === primero) {
          evento.preventDefault();
          ultimo?.focus();
        } else if (!evento.shiftKey && document.activeElement === ultimo) {
          evento.preventDefault();
          primero?.focus();
        }
      }}
    >
      <div
        ref={contenedor}
        className="dialogo-confirmacion"
        role="dialog"
        aria-modal="true"
        aria-labelledby="titulo-dialogo-cancelar"
        aria-describedby="detalle-dialogo-cancelar"
      >
        <span className="icono-dialogo" aria-hidden="true">
          <svg viewBox="0 0 24 24">
            <path d="M12 8v5m0 3.5v.5M10.3 4.2 3.1 17a2 2 0 0 0 1.7 3h14.4a2 2 0 0 0 1.7-3L13.7 4.2a2 2 0 0 0-3.4 0Z" />
          </svg>
        </span>
        <h3 id="titulo-dialogo-cancelar">{textos.misCitas.confirmarCancelacion}</h3>
        <p id="detalle-dialogo-cancelar">{textos.misCitas.confirmarCancelacionAyuda}</p>
        <dl className="detalle-cancelacion">
          <div>
            <dt>{textos.resumenReserva.servicio}</dt>
            <dd>{cita.servicio.nombre}</dd>
          </div>
          <div>
            <dt>{textos.resumenReserva.cuando}</dt>
            <dd>{instanteLargo(cita.inicio, zona)}</dd>
          </div>
        </dl>
        <div className="botonera">
          <button ref={botonPrincipal} type="button" onClick={alCerrar} disabled={cancelando}>
            {textos.misCitas.mantener}
          </button>
          <button type="button" className="peligro" onClick={alConfirmar} disabled={cancelando}>
            {cancelando ? textos.misCitas.cancelando : textos.misCitas.confirmarCancelar}
          </button>
        </div>
      </div>
    </div>
  );
}
